import {
  useFieldSequenceAdminView,
  useSetFieldSequenceSetting,
} from "../../hooks/field-sequence.hooks";
import { SuccessToast } from "../toastMessage";
import style from "./FieldSequence.module.css";

/**
 * Which screens a switch controls: the applicant's (the applicant form, the
 * applicant's View Application, their payment details screen and the
 * resubmit preview), or the reviewers' (L1/L2/L3 application
 * details, the Admin/reviewer View Application and their PDF).
 * Each switch is stored on its own, so turning one off leaves the other as
 * it is.
 */
export type TFieldSequenceTarget = "form" | "review";

const TARGETS = {
  form: {
    isOn: (data?: { enabled?: boolean }) => Boolean(data?.enabled),
    body: (enabled: boolean) => ({ enabled }),
    offMessage: "The applicant's screens now use the default order",
    onMessage: "The applicant's screens now follow the set sequence",
  },
  review: {
    isOn: (data?: { reviewEnabled?: boolean }) => Boolean(data?.reviewEnabled),
    body: (reviewEnabled: boolean) => ({ reviewEnabled }),
    offMessage: "The reviewers' screens now use the default order",
    onMessage: "The reviewers' screens now follow the set sequence",
  },
} as const;

/**
 * One on/off switch of the Field Sequence feature. On means the screens of
 * `target` follow the order saved below; off means their default order.
 *
 * BEFORE - one switch, for the applicant form only:
 *   export default function FieldSequenceToggle() {
 *     const isOn = Boolean(data?.enabled);
 *     setSetting({ enabled: !isOn }, ...)
 *     "The applicant form now uses the default order" /
 *     "The applicant form now follows the set sequence"
 *   }
 */
export default function FieldSequenceToggle({
  target = "form",
}: {
  target?: TFieldSequenceTarget;
}) {
  const { data, isLoading } = useFieldSequenceAdminView();
  const { mutate: setSetting, isPending } = useSetFieldSequenceSetting();
  const config = TARGETS[target];
  const isOn = config.isOn(data);

  const onToggle = () =>
    setSetting(config.body(!isOn), {
      onSuccess: () => SuccessToast(isOn ? config.offMessage : config.onMessage),
    });

  return (
    <label className={style.toggleBtn}>
      <input
        type="checkbox"
        className={style.inputCheckbox}
        checked={isOn}
        disabled={isLoading || isPending}
        onChange={onToggle}
      />
      <span className={style.slider}></span>
    </label>
  );
}
